import { apiClient } from './client';
import type {
  CashDrawerConfig,
  Device,
  DeviceCreateInput,
  DeviceUpdateInput,
  PairingCodeResponse,
  PrinterConfig,
} from './types';

interface DeviceWorkerResponse {
  id: string;
  name: string;
  type: 'ipad' | 'iphone' | 'android_tablet' | 'android_phone' | 'terminal';
  status: 'pending' | 'active' | 'disabled';
  locationId?: number;
  apiKey: string;
  lastActiveAt?: string;
  registeredAt: string;
  active: boolean;
  printer?: PrinterConfig;
  cashDrawer?: CashDrawerConfig;
}

interface PaginationInfo {
  total: number;
  count: number;
  perPage: number;
  currentPage: number;
  totalPages: number;
}

interface DeviceListParams {
  status?: 'pending' | 'active' | 'disabled';
  locationId?: number;
  page?: number;
  limit?: number;
}

export const devicesApi = {
  async list(params: DeviceListParams = {}): Promise<{ devices: Device[]; pagination: PaginationInfo }> {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.set('status', params.status);
    if (params.locationId) searchParams.set('locationId', String(params.locationId));
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));

    const query = searchParams.toString();
    const response = await apiClient.request<{
      devices: DeviceWorkerResponse[];
      pagination: PaginationInfo;
    }>(`/devices${query ? `?${query}` : ''}`);

    return {
      devices: response.devices.map(d => ({
        id: d.id,
        name: d.name,
        type: d.type,
        status: d.status,
        locationId: d.locationId,
        apiKey: d.apiKey,
        lastHeartbeat: d.lastActiveAt || null,
        isActive: d.active,
        createdAt: d.registeredAt,
        updatedAt: d.registeredAt,
        printer: d.printer,
        cashDrawer: d.cashDrawer,
      })),
      pagination: response.pagination,
    };
  },

  async create(data: DeviceCreateInput): Promise<Device> {
    const response = await apiClient.request<DeviceWorkerResponse>('/devices', {
      method: 'POST',
      body: data,
    });

    return {
      id: response.id,
      apiKey: response.apiKey,
      name: response.name,
      type: response.type,
      locationId: response.locationId,
      lastHeartbeat: null,
      isActive: response.active,
      createdAt: response.registeredAt,
      updatedAt: response.registeredAt,
      printer: response.printer,
      cashDrawer: response.cashDrawer,
    };
  },

  async update(id: string, data: DeviceUpdateInput): Promise<{ success: boolean }> {
    const workerData: Record<string, unknown> = { ...data };
    if ('isActive' in data) {
      workerData.active = data.isActive;
      delete workerData.isActive;
    }
    return apiClient.request<{ success: boolean }>(`/devices/${id}`, {
      method: 'PUT',
      body: workerData,
    });
  },

  async delete(id: string): Promise<{ success: boolean }> {
    return apiClient.request<{ success: boolean }>(`/devices/${id}`, {
      method: 'DELETE',
    });
  },

  async regenerateKey(id: string): Promise<{ apiKey: string }> {
    const response = await apiClient.request<{
      id: string;
      apiKey: string;
      name: string;
      type: string;
    }>(`/devices/${id}/regenerate-key`, { method: 'POST' });
    return { apiKey: response.apiKey };
  },

  async createPairingCode(data: {
    name: string;
    type: 'ipad' | 'iphone' | 'android_tablet' | 'android_phone' | 'terminal';
    locationId?: number;
  }): Promise<PairingCodeResponse> {
    return apiClient.request<PairingCodeResponse>('/devices/pairing-code', {
      method: 'POST',
      body: data,
    });
  },

  async regeneratePairingCode(id: string): Promise<Omit<PairingCodeResponse, 'device'>> {
    return apiClient.request<Omit<PairingCodeResponse, 'device'>>(
      `/devices/${id}/regenerate-pairing-code`,
      { method: 'POST' }
    );
  },

  async getInfo(id: string): Promise<{
    id: string;
    name: string;
    type: string;
    locationId?: number;
    locationName?: string;
    status: 'pending' | 'active' | 'disabled';
    registeredAt: string;
    lastActiveAt?: string;
  }> {
    return apiClient.request(`/devices/${id}/info`);
  },

  async reconnect(id: string): Promise<PairingCodeResponse> {
    return apiClient.request<PairingCodeResponse>(`/devices/${id}/reconnect`, {
      method: 'POST',
    });
  },
};
